import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Spinner, Alert, Badge, Form, InputGroup } from 'react-bootstrap';
import inboundApi from '../../config/inboundApi';
import AddressFormModal from './components/AddressFormModal';

export default function SupportAddressesTab() {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editAddress, setEditAddress] = useState(null);

  const fetchAddresses = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await inboundApi.get('/support-addresses', { params });
      setAddresses(res.data);
    } catch (err) {
      setMessage({ type: 'danger', text: 'Failed to load support addresses' });
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this address?')) return;
    try {
      await inboundApi.delete(`/support-addresses/${id}`);
      setMessage({ type: 'success', text: 'Address deleted' });
      fetchAddresses();
    } catch (err) {
      setMessage({ type: 'danger', text: 'Failed to delete address' });
    }
  };

  const handleEdit = (addr) => {
    setEditAddress(addr);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditAddress(null);
    setShowModal(true);
  };

  const handleModalClose = (saved) => {
    setShowModal(false);
    setEditAddress(null);
    if (saved) {
      setMessage({ type: 'success', text: saved === 'created' ? 'Address created' : 'Address updated' });
      fetchAddresses();
    }
  };

  const handleToggleDefault = async (addr) => {
    try {
      await inboundApi.put(`/support-addresses/${addr.id}`, { is_default: !addr.is_default });
      fetchAddresses();
    } catch (err) {
      setMessage({ type: 'danger', text: 'Failed to update address' });
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <InputGroup style={{ maxWidth: 300 }}>
          <Form.Control
            placeholder="Search addresses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Button variant="primary" onClick={handleAdd}>
          Add Address
        </Button>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : addresses.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No support addresses yet. Click "Add Address" to create one.
            </div>
          ) : (
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Address</th>
                  <th>Display Name</th>
                  <th>Status</th>
                  <th>Default</th>
                  <th>Created</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {addresses.map((addr) => (
                  <tr key={addr.id}>
                    <td>
                      <strong>{addr.address}</strong>
                    </td>
                    <td>{addr.display_name}</td>
                    <td>
                      <Badge bg={addr.status === 'active' ? 'success' : 'secondary'}>
                        {addr.status}
                      </Badge>
                    </td>
                    <td>
                      <Form.Check
                        type="switch"
                        checked={addr.is_default}
                        onChange={() => handleToggleDefault(addr)}
                      />
                    </td>
                    <td>
                      <small className="text-muted">
                        {new Date(addr.created_at).toLocaleDateString()}
                      </small>
                    </td>
                    <td className="text-end">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(addr)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(addr.id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <AddressFormModal
        show={showModal}
        onClose={handleModalClose}
        editAddress={editAddress}
      />
    </div>
  );
}
